import axios from 'axios';

const API_BASE_URL = '/api';

const api = axios.create({
  baseURL: API_BASE_URL,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Auth API
export const authAPI = {
  login: (username, password, extra = {}) => api.post('/auth/login', { username, password, ...extra }),
  logout: () => api.post('/auth/logout'),
  register: (registerData) => api.post('/auth/register', registerData),
  setupProfile: (username, email, password, firstname, surname) => api.post('/auth/setup-profile', { username, email, password, firstname, surname }),
  checkAuth: () => api.get('/auth/check-auth'),
  getCurrentUser: () => api.get('/auth/me'),
  // Password reset
  requestPasswordReset: (email) => api.post('/auth/password-reset/request', { email }),
  verifyResetToken: (token) => api.post('/auth/password-reset/verify', { token }),
  resetPassword: (token, password) => api.post('/auth/password-reset/reset', { token, password }),
  checkUsername: (username) => api.post('/auth/check-username', { username }),
  checkEmail: (email) => api.post('/auth/check-email', { email }),
  validateToken: (token) => api.post('/auth/check-token', { token }),
};

// Company API
export const companyAPI = {
  getCompanyInfo: () => api.get('/company/info'),
  updateCompanyInfo: (companyData) => api.put('/company/info', companyData),
  getCompanyUsers: () => api.get('/company/users'),
  getCompanyStats: () => api.get('/company/stats'),
  getActiveCompanies: () => api.get('/company/active'),
  // Team management (admins and technicians)
  createCompanyUser: (userData) => api.post('/company/users', userData),
  updateCompanyUser: (userId, userData) => api.put(`/company/users/${userId}`, userData),
  deactivateCompanyUser: (userId) => api.delete(`/company/users/${userId}`),
  getCompanyAdmins: () => api.get('/company/users/admins'),
  getCompanyTechnicians: () => api.get('/company/users/technicians'),
  // Bike blocking
  blockBike: (bikeId, reason) => api.post(`/company/bikes/${bikeId}/block`, { reason }),
  unblockBike: (bikeId) => api.post(`/company/bikes/${bikeId}/unblock`),
  getBlockedBikes: () => api.get('/company/blocked-bikes'),
  // Super admin routes
  getAllCompanies: () => api.get('/company/admin/companies'),
  getCompanyDetails: (companyId) => api.get(`/company/admin/companies/${companyId}`),
  activateCompany: (companyId, isActive) => api.put(`/company/admin/companies/${companyId}/activate`, { is_active: isActive }),
  deleteCompany: (companyId) => api.delete(`/company/admin/companies/${companyId}`),
  geocodeCompany: (companyId) => api.post(`/company/admin/companies/${companyId}/geocode`),
  // Registration Tokens
  getRegistrationTokens: () => api.get('/admin/registration-tokens'),
  generateRegistrationToken: (note) => api.post('/admin/registration-tokens', { note }),
  fixServicesSchema: () => api.post('/admin/fix-services-schema'),
  getCompanyPublic: (companyId) => api.get(`/companies/${companyId}/public`),
  getCompanyPublicProfile: (companyId) =>
    api.get(`/companies/${companyId}/public`).then(res => ({ data: res.data?.company ?? res.data })),
  getCompanyList: (params) => api.get('/companies/list', { params }),
  getCompanyMap: (params) => api.get('/companies/map', { params }),
  // Application settings
  getAppSettings: () => api.get('/admin/settings'),
  updateAppSettings: (settings) => api.put('/admin/settings', settings),
  // App logo (superadmin only for upload/delete, public for get)
  getAppLogo: () => api.get('/admin/settings/app-logo'),
  uploadAppLogo: (formData) => api.post('/admin/settings/app-logo', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  deleteAppLogo: () => api.delete('/admin/settings/app-logo'),
  // Logo upload
  uploadLogo: (formData, companyId) => {
    if (companyId) formData.append('company_id', companyId);
    return api.post('/company/logo', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  deleteLogo: (companyId) => api.delete('/company/logo', { params: { company_id: companyId } }),
};

// Admin API
export const adminAPI = {
  // Bikes
  getAllBikes: (params) => api.get('/admin/bikes', { params }),
  getBike: (bikeId) => api.get(`/admin/bikes/${bikeId}`),
  addBike: (bikeData) => api.post('/admin/bikes', bikeData),
  updateBike: (bikeId, bikeData) => api.put(`/admin/bikes/${bikeId}`, bikeData),
  deleteBike: (bikeId) => api.delete(`/admin/bikes/${bikeId}`),
  // Bike company registrations
  registerBikeToCompany: (vin, notes) => api.post('/admin/bikes/register-to-company', { vin, notes }),
  getRegisteredBikes: () => api.get('/admin/bikes/registered'),
  // Bike images (admin)
  getBikeImages: (bikeId) => api.get(`/admin/bikes/${bikeId}/images`),
  uploadBikeImage: (bikeId, formData) => api.post(`/admin/bikes/${bikeId}/images`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  deleteBikeImage: (bikeId, imageId) => api.delete(`/admin/bikes/${bikeId}/images/${imageId}`),
  setBikeProfileImage: (bikeId, imageId) => api.post(`/admin/bikes/${bikeId}/images/${imageId}/set-profile`),
  // Bike comments (admin)
  getBikeComments: (bikeId) => api.get(`/admin/bikes/${bikeId}/comments`),
  addBikeComment: (bikeId, data) => api.post(`/admin/bikes/${bikeId}/comments`, data),

  // Services
  getAllServices: (params) => api.get('/admin/services', { params }),
  getBikeServices: (bikeId) => api.get(`/admin/bikes/${bikeId}/services`),
  addService: (formData) => api.post('/admin/bikes/0/services', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }),
  updateService: (serviceId, serviceData) => api.put(`/admin/services/${serviceId}`, serviceData),
  deleteService: (serviceId) => api.delete(`/admin/services/${serviceId}`),
  uploadServiceReceipts: (serviceId, formData) => api.post(`/admin/services/${serviceId}/receipts`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }),
  sendServiceCompletionNotification: (serviceId) => api.post(`/admin/services/${serviceId}/send-completion-notification`),
  // User service endpoints
  updateServiceDescription: (bikeId, serviceId, description) => api.put(`/user/bikes/${bikeId}/services/${serviceId}/description`, { description }),

  // Users
  getAllUsers: (params) => api.get('/admin/users', { params }),
  getUser: (userId) => api.get(`/admin/users/${userId}`),
  createUser: (userData) => api.post('/admin/users', userData),
  updateUser: (userId, userData) => api.put(`/admin/users/${userId}`, userData),
  deactivateUser: (userId) => api.post(`/admin/users/${userId}/deactivate`),
  activateUser: (userId) => api.post(`/admin/users/${userId}/activate`),
  deleteUser: (userId) => api.delete(`/admin/users/${userId}`),
  assignBikeToUser: (userId, bikeId) => api.post(`/admin/users/${userId}/bikes/${bikeId}`),

  // Analytics
  getAnalyticsOverview: () => api.get('/analytics/admin/overview'),
  getRevenueByMonth: (params) => api.get('/analytics/admin/revenue-by-month', { params }),
  getTopBikes: (params) => api.get('/analytics/admin/top-bikes', { params }),
};

// User API
export const userAPI = {
  getMyBikes: () => api.get('/user/bikes'),
  getMyBike: (bikeId) => api.get(`/user/bikes/${bikeId}`),
  createBike: (bikeData) => api.post('/user/bikes', bikeData),
  transferBike: (bikeId, recipient) => api.post(`/user/bikes/${bikeId}/transfer`, { recipient }),
  getProfile: () => api.get('/user/profile'),
  updateProfile: (profileData) => api.put('/user/profile', profileData),
  uploadProfileImage: (formData) => api.post('/user/profile/image', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  getNotifications: (params) => api.get('/user/notifications', { params }),
  markNotificationRead: (notificationId) => api.post(`/user/notifications/${notificationId}/read`),
  getReceipt: (filename) => `/api/user/receipts/${filename}`,
  // Bike company registrations
  getBikeCompanyRegistrations: (bikeId) => api.get(`/user/bikes/${bikeId}/companies`),
  addBikeToCompany: (bikeId, companyId, notes) => api.post(`/user/bikes/${bikeId}/companies`, { company_id: companyId, notes }),
  updateBikeCompanyRegistration: (bikeId, companyId, isActive, notes) => api.put(`/user/bikes/${bikeId}/companies/${companyId}`, { is_active: isActive, notes }),
  removeBikeCompanyRegistration: (bikeId, companyId) => api.delete(`/user/bikes/${bikeId}/companies/${companyId}`),
  // Bike notes
  updateBikeNotes: (bikeId, notes) => api.put(`/user/bikes/${bikeId}/notes`, { notes }),
  updateBikeDescription: (bikeId, description) => api.put(`/user/bikes/${bikeId}/description`, { description }),
  // Service description
  updateServiceDescription: (bikeId, serviceId, description) => api.put(`/user/bikes/${bikeId}/services/${serviceId}/description`, { description }),
  // Bike images
  getBikeGallery: (bikeId) => api.get(`/user/bikes/${bikeId}/gallery`),
  uploadBikeImage: (bikeId, formData) => api.post(`/user/bikes/${bikeId}/images`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  deleteBikeImage: (bikeId, imageId) => api.delete(`/user/bikes/${bikeId}/images/${imageId}`),
  setBikeProfileImage: (bikeId, imageId) => api.post(`/user/bikes/${bikeId}/images/${imageId}/set-profile`),
  updateBikeImageCaption: (bikeId, imageId, caption) => api.put(`/user/bikes/${bikeId}/images/${imageId}/caption`, { caption }),
  // Bike profile image (direct upload)
  uploadBikeProfileImage: (bikeId, formData) => api.post(`/user/bikes/${bikeId}/profile-image`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  // Stolen bike reporting
  reportBikeStolen: (bikeId) => api.post(`/user/bikes/${bikeId}/report-stolen`),
  reportBikeFound: (bikeId) => api.post(`/user/bikes/${bikeId}/report-found`),
  // Annual service reminders
  getAnnualReminderStatus: () => api.get('/user/bikes/annual-reminders'),
  // Service receipt upload
  uploadServiceReceipt: (bikeId, serviceId, formData) => api.post(`/user/bikes/${bikeId}/services/${serviceId}/receipt`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  // AI extract from receipt (for users)
  extractFromReceipt: (formData) => api.post('/user/receipt/extract', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
    timeout: 90000, // 90 seconds for AI extraction
  }),
  // Add service items (from AI extraction)
  addServiceItems: (bikeId, serviceId, data) => api.post(`/user/bikes/${bikeId}/services/${serviceId}/items`, data),
  // Service verification (user not connected to company that serviced their bike)
  getPendingServiceVerifications: () => api.get('/user/pending-service-verifications'),
  confirmServiceFromCompany: (serviceId, accept) => api.post('/user/confirm-service-from-company', { service_id: serviceId, accept }),
  // Delete own account (bikes and services retained, personal data removed)
  deleteOwnAccount: () => api.post('/user/delete-account', { confirm: 'DELETE_MY_ACCOUNT' }),
};

// Bike Comments API
export const bikeCommentsAPI = {
  getComments: (bikeId) => api.get(`/bikes/${bikeId}/comments`),
  addComment: (bikeId, data) => api.post(`/bikes/${bikeId}/comments`, data),
  updateComment: (bikeId, commentId, data) => api.put(`/bikes/${bikeId}/comments/${commentId}`, data),
  deleteComment: (bikeId, commentId) => api.delete(`/bikes/${bikeId}/comments/${commentId}`),
  getCommentHistory: (bikeId, commentId) => api.get(`/bikes/${bikeId}/comments/${commentId}/history`),
  togglePinComment: (bikeId, commentId) => api.post(`/bikes/${bikeId}/comments/${commentId}/pin`),
};

// Payment API
export const paymentAPI = {
  getSubscription: () => api.get('/payment/subscription'),
  createSubscription: (planTier) => api.post('/payment/subscription', { plan_tier: planTier }),
  cancelSubscription: (cancelAtPeriodEnd = true) => api.post('/payment/subscription/cancel', { cancel_at_period_end: cancelAtPeriodEnd }),
  getPayments: () => api.get('/payment/payments'),
  getInvoices: () => api.get('/payment/invoices'),
  // New tier system: free (bike owner), basic, professional, enterprise (bike shops)
  createCheckoutSession: (planTier, source = 'onboarding') =>
    api.post('/payment/checkout-session', { plan_tier: planTier, source }),
  // TEMPORARY: Activate free trial without payment (until LemonSqueezy demo video approved)
  activateFreeTrial: (planTier) => api.post('/payment/activate-free-trial', { plan_tier: planTier }),
  // Lemon Squeezy subscription management
  getLemonSqueezySubscription: () => api.get('/payment/lemonsqueezy/subscription'),
  syncLemonSqueezySubscription: (expectedPlan) =>
    api.post('/payment/lemonsqueezy/sync-from-provider', { expected_plan: expectedPlan }),
  cancelLemonSqueezySubscription: () => api.post('/payment/lemonsqueezy/cancel'),
  resumeLemonSqueezySubscription: () => api.post('/payment/lemonsqueezy/resume'),
  // switchPlan supports: basic, professional, enterprise
  switchPlan: (plan) => api.post('/payment/lemonsqueezy/switch-plan', { plan }),
  getCustomerPortal: () => api.get('/payment/lemonsqueezy/customer-portal'),
  // Super admin billing management
  getAllSubscriptions: (params) => api.get('/payment/super/subscriptions', { params }),
  getAllPayments: (params) => api.get('/payment/super/payments', { params }),
  getAllInvoices: (params) => api.get('/payment/super/invoices', { params }),
  // Tier information
  getTierInfo: () => api.get('/payment/tiers'),
  checkFeatureAccess: (feature) => api.get(`/payment/feature-check/${feature}`),
};

// Booking API
export const bookingAPI = {
  // Public endpoints
  getAvailableSlots: (companyId, date, serviceTemplateId, technicianId) =>
    api.get(`/booking/companies/${companyId}/available-slots`, {
      params: { date, service_template_id: serviceTemplateId, technician_id: technicianId }
    }),
  getCompanyTechniciansPublic: (companyId) =>
    api.get(`/booking/companies/${companyId}/technicians`),
  createBooking: (companyId, bookingData) =>
    api.post(`/booking/companies/${companyId}/book`, bookingData),

  // User endpoints
  getUserBookings: () => api.get('/booking/user/bookings'),
  getUserBooking: (bookingId) => api.get(`/booking/user/bookings/${bookingId}`),
  cancelBooking: (bookingId) => api.put(`/booking/user/bookings/${bookingId}/cancel`),

  // Booking interest (when company doesn't have booking enabled)
  sendBookingInterest: (companyId) => api.post(`/booking/companies/${companyId}/interest`),
  getBookingInterests: () => api.get('/booking/admin/booking-interests'),

  // Admin endpoints
  getAllBookings: (filters) => api.get('/booking/admin/bookings', { params: filters }),
  getBookingDetails: (bookingId) => api.get(`/booking/admin/bookings/${bookingId}`),
  updateBookingStatus: (bookingId, status, serviceId) =>
    api.put(`/booking/admin/bookings/${bookingId}/status`, { status, ...(serviceId ? { service_id: serviceId } : {}) }),
  updateBooking: (bookingId, data) => api.put(`/booking/admin/bookings/${bookingId}`, data),
  completeBooking: (bookingId) => api.post(`/booking/admin/bookings/${bookingId}/complete`),

  // Service templates
  getServiceTemplates: (activeOnly, companyId) => api.get('/booking/admin/service-templates', { params: { active_only: activeOnly, company_id: companyId } }),
  getCompanyServiceTemplates: (companyId, activeOnly) => api.get(`/booking/companies/${companyId}/service-templates`, { params: { active_only: activeOnly } }),
  createServiceTemplate: (templateData) => api.post('/booking/admin/service-templates', templateData),
  updateServiceTemplate: (templateId, templateData) => api.put(`/booking/admin/service-templates/${templateId}`, templateData),
  deleteServiceTemplate: (templateId) => api.delete(`/booking/admin/service-templates/${templateId}`),

  // Technicians
  getTechnicians: (activeOnly, companyId) => api.get('/booking/admin/technicians', { params: { active_only: activeOnly, company_id: companyId } }),
  createTechnician: (technicianData) => api.post('/booking/admin/technicians', technicianData),
  updateTechnician: (technicianId, technicianData) => api.put(`/booking/admin/technicians/${technicianId}`, technicianData),
  deleteTechnician: (technicianId) => api.delete(`/booking/admin/technicians/${technicianId}`),

  // Working hours
  getWorkingHours: (companyId) => api.get('/booking/admin/working-hours', { params: { company_id: companyId } }),
  updateWorkingHours: (workingHours, companyId) => api.put('/booking/admin/working-hours', { working_hours: workingHours, company_id: companyId }),

  // Technician endpoints
  getTechnicianBookings: (filters) => api.get('/booking/technician/bookings', { params: filters }),
  updateTechnicianBookingStatus: (bookingId, status, notes) =>
    api.put(`/booking/technician/bookings/${bookingId}/status`, { status, notes }),
  completeTechnicianBookingDraft: (bookingId, data) =>
    api.post(`/booking/technician/bookings/${bookingId}/complete-draft`, data),
  getTechnicianProfile: () => api.get('/booking/technician/profile'),
  updateTechnicianProfile: (profileData) => api.put('/booking/technician/profile', profileData),

  // Technician time off (admin)
  getTechnicianTimeOff: (technicianId) => api.get(`/booking/admin/technicians/${technicianId}/time-off`),
  addTechnicianTimeOff: (technicianId, data) => api.post(`/booking/admin/technicians/${technicianId}/time-off`, data),
  deleteTechnicianTimeOff: (timeOffId) => api.delete(`/booking/admin/technicians/time-off/${timeOffId}`),

  // Technician time off (self)
  getMyTimeOff: () => api.get('/booking/technician/time-off'),
  addMyTimeOff: (data) => api.post('/booking/technician/time-off', data),
  deleteMyTimeOff: (timeOffId) => api.delete(`/booking/technician/time-off/${timeOffId}`),

  // Time-off delegation
  checkAvailabilityForTimeOff: (data) => api.post('/booking/technician/time-off/check-availability', data),
  getBookingsToDelegate: (data) => api.post('/booking/technician/time-off/bookings-to-delegate', data),
  getBookingAlternativeSlots: (bookingId) => api.get(`/booking/technician/booking/${bookingId}/alternative-slots`),
  rescheduleBookingAsTechnician: (bookingId, data) => api.post(`/booking/technician/booking/${bookingId}/reschedule`, data),
  getMyDelegations: () => api.get('/booking/technician/delegations'),
  respondToDelegation: (delegationId, action) => api.post(`/booking/technician/delegations/${delegationId}/respond`, { action }),

  // Admin - vacation days
  updateTechnicianVacationDays: (technicianId, vacationDays) => api.put(`/booking/admin/technicians/${technicianId}/vacation-days`, { vacation_days_allowed: vacationDays }),

  // Catch-up days (no bike required)
  getCatchUpDays: (params) => api.get('/booking/admin/catch-up-days', { params }),
  createCatchUpDay: (data) => api.post('/booking/admin/catch-up-days', data),
  deleteCatchUpDay: (dayId) => api.delete(`/booking/admin/catch-up-days/${dayId}`),

  // Assignment logic help
  getAssignmentLogicInfo: () => api.get('/booking/admin/assignment-logic'),
};

// Service Room API
export const serviceRoomAPI = {
  getServiceRooms: (activeOnly, companyId) => api.get('/service-rooms/', { params: { active_only: activeOnly, company_id: companyId } }),
  getServiceRoom: (roomId) => api.get(`/service-rooms/${roomId}`),
  createServiceRoom: (roomData) => api.post('/service-rooms/', roomData),
  updateServiceRoom: (roomId, roomData) => api.put(`/service-rooms/${roomId}`, roomData),
  deleteServiceRoom: (roomId) => api.delete(`/service-rooms/${roomId}`),
  getRoomWorkingHours: (roomId) => api.get(`/service-rooms/${roomId}/working-hours`),
  updateRoomWorkingHours: (roomId, workingHours) => api.put(`/service-rooms/${roomId}/working-hours`, { working_hours: workingHours }),
};

// User-Company Connection API
export const userCompanyAPI = {
  // User endpoints
  getAvailableCompanies: () => api.get('/user-company/companies'),
  getUserConnections: () => api.get('/user-company/connections'),
  requestConnection: (companyId) => api.post('/user-company/connections/request', { company_id: companyId }),
  disconnectFromCompany: (connectionId) => api.post(`/user-company/connections/${connectionId}/disconnect`),

  // Company admin endpoints
  getPendingRequests: () => api.get('/user-company/admin/pending-requests'),
  getCompanyConnections: (status) => api.get('/user-company/admin/connections', { params: { status } }),
  acceptConnection: (connectionId) => api.post(`/user-company/admin/connections/${connectionId}/accept`),
  rejectConnection: (connectionId, notes) => api.post(`/user-company/admin/connections/${connectionId}/reject`, { notes }),
  removeUserConnection: (connectionId) => api.post(`/user-company/admin/connections/${connectionId}/remove`),
  getConnectedUsers: () => api.get('/user-company/admin/users'),
  addUserToCompany: (identifier) => api.post('/user-company/admin/users/add', { username: identifier, email: identifier }),
};

// Marketing API
export const marketingAPI = {
  getCampaigns: () => api.get('/marketing/campaigns'),
  getCampaign: (campaignId) => api.get(`/marketing/campaigns/${campaignId}`),
  createCampaign: (campaignData) => api.post('/marketing/campaigns', campaignData),
  sendCampaign: (campaignId) => api.post(`/marketing/campaigns/${campaignId}/send`),
  deleteCampaign: (campaignId) => api.delete(`/marketing/campaigns/${campaignId}`),
  getTemplates: (type) => api.get('/marketing/templates', { params: { type } }),
  createTemplate: (templateData) => api.post('/marketing/templates', templateData),

  // Promotional Emails (monthly, requires superadmin approval)
  getPromotionalEmails: () => api.get('/marketing/promotional-emails'),
  getPromotionalEmail: (emailId) => api.get(`/marketing/promotional-emails/${emailId}`),
  createPromotionalEmail: (data) => api.post('/marketing/promotional-emails', data),
  updatePromotionalEmail: (emailId, data) => api.put(`/marketing/promotional-emails/${emailId}`, data),
  deletePromotionalEmail: (emailId) => api.delete(`/marketing/promotional-emails/${emailId}`),
  submitPromotionalEmail: (emailId) => api.post(`/marketing/promotional-emails/${emailId}/submit`),
  previewPromotionalRecipients: (emailId) => api.get(`/marketing/promotional-emails/${emailId}/preview-recipients`),

  // Brand filtering and campaign status
  getAvailableBrands: (companyId) => api.get('/marketing/available-brands', { params: companyId ? { company_id: companyId } : {} }),
  previewRecipientsByFilter: (params) => api.get('/marketing/recipients-preview', { params }),
  getCampaignStatus: (companyId) => api.get('/marketing/campaign-status', { params: companyId ? { company_id: companyId } : {} }),
  getGlobalTemplate: (companyId) => api.get('/marketing/global-template', { params: companyId ? { company_id: companyId } : {} }),
};

// Super Admin API
export const superAdminAPI = {
  // Users
  getAllUsers: (params) => api.get('/admin/super/users', { params }),
  getUser: (userId) => api.get(`/admin/super/users/${userId}`),
  updateUser: (userId, data) => api.put(`/admin/super/users/${userId}`, data),
  deleteUser: (userId) => api.delete(`/admin/super/users/${userId}`),

  // Bikes
  getAllBikes: (params) => api.get('/admin/super/bikes', { params }),
  getBike: (bikeId) => api.get(`/admin/super/bikes/${bikeId}`),
  updateBike: (bikeId, data) => api.put(`/admin/super/bikes/${bikeId}`, data),
  deleteBike: (bikeId) => api.delete(`/admin/super/bikes/${bikeId}`),
  transferBike: (bikeId, recipient) => api.post(`/admin/super/bikes/${bikeId}/transfer`, { recipient }),

  // Services
  getAllServices: (params) => api.get('/admin/super/services', { params }),
  getService: (serviceId) => api.get(`/admin/super/services/${serviceId}`),
  updateService: (serviceId, data) => api.put(`/admin/super/services/${serviceId}`, data),
  deleteService: (serviceId) => api.delete(`/admin/super/services/${serviceId}`),

  // Bookings
  getAllBookings: (params) => api.get('/admin/super/bookings', { params }),
  getBooking: (bookingId) => api.get(`/admin/super/bookings/${bookingId}`),
  updateBooking: (bookingId, data) => api.put(`/admin/super/bookings/${bookingId}`, data),
  deleteBooking: (bookingId) => api.delete(`/admin/super/bookings/${bookingId}`),

  // Analytics
  getAnalytics: (params) => api.get('/admin/super/analytics', { params }),

  // Broadcast Emails
  getBroadcasts: () => api.get('/admin/super/broadcasts'),
  getBroadcast: (broadcastId) => api.get(`/admin/super/broadcasts/${broadcastId}`),
  createBroadcast: (data) => api.post('/admin/super/broadcasts', data),
  updateBroadcast: (broadcastId, data) => api.put(`/admin/super/broadcasts/${broadcastId}`, data),
  deleteBroadcast: (broadcastId) => api.delete(`/admin/super/broadcasts/${broadcastId}`),
  previewBroadcastRecipients: (broadcastId) => api.get(`/admin/super/broadcasts/${broadcastId}/preview`),
  sendBroadcast: (broadcastId) => api.post(`/admin/super/broadcasts/${broadcastId}/send`),

  // Global Marketing Templates
  getGlobalMarketingTemplates: () => api.get('/admin/super/marketing-templates'),
  getGlobalMarketingTemplate: (id) => api.get(`/admin/super/marketing-templates/${id}`),
  createGlobalMarketingTemplate: (data) => api.post('/admin/super/marketing-templates', data),
  updateGlobalMarketingTemplate: (id, data) => api.put(`/admin/super/marketing-templates/${id}`, data),
  deleteGlobalMarketingTemplate: (id) => api.delete(`/admin/super/marketing-templates/${id}`),

  // Marketing Campaigns (bulk email, custom list, contact@frame-book.com)
  getMarketingCampaigns: () => api.get('/admin/super/marketing-campaigns'),
  getMarketingTemplate: () => api.get('/admin/super/marketing-campaigns/template'),
  getMarketingCampaign: (id) => api.get(`/admin/super/marketing-campaigns/${id}`),
  createMarketingCampaign: (data) => api.post('/admin/super/marketing-campaigns', data),
  updateMarketingCampaign: (id, data) => api.put(`/admin/super/marketing-campaigns/${id}`, data),
  deleteMarketingCampaign: (id) => api.delete(`/admin/super/marketing-campaigns/${id}`),
  formatMarketingCampaignContent: (id, data) => api.post(`/admin/super/marketing-campaigns/${id}/format`, data),
  sendMarketingCampaign: (id) => api.post(`/admin/super/marketing-campaigns/${id}/send`),
  getMarketingCampaignSendStatus: (id) => api.get(`/admin/super/marketing-campaigns/${id}/send-status`),
  getMarketingCampaignResendCount: (id) => api.get(`/admin/super/marketing-campaigns/${id}/resend-count`),
  getMarketingCampaignAnalytics: (id) => api.get(`/admin/super/marketing-campaigns/${id}/analytics`),
  setupResendAnalytics: () => api.post('/admin/super/marketing-campaigns/setup-resend'),

  // Promotional Email Approvals
  getPendingPromotionalEmails: () => api.get('/admin/super/promotional-emails'),
  getPromotionalEmail: (emailId) => api.get(`/admin/super/promotional-emails/${emailId}`),
  approvePromotionalEmail: (emailId) => api.post(`/admin/super/promotional-emails/${emailId}/approve`),
  rejectPromotionalEmail: (emailId, reason) => api.post(`/admin/super/promotional-emails/${emailId}/reject`, { reason }),
};

// Service Reviews API
export const reviewAPI = {
  listForService: (serviceId) => api.get(`/services/${serviceId}/reviews`),
  createForService: (serviceId, data) => api.post(`/services/${serviceId}/reviews`, data),
  updateReview: (reviewId, data) => api.put(`/reviews/${reviewId}`, data),
  deleteReview: (reviewId) => api.delete(`/reviews/${reviewId}`),
  listForCompany: (companyId) => api.get(`/companies/${companyId}/reviews`),
  getCompanyStats: (companyId) => api.get(`/companies/${companyId}/reviews/stats`),
  moderateReview: (reviewId, data) => api.put(`/admin/reviews/${reviewId}/moderate`, data),
  // Company admin endpoints
  getCompanyReviews: (params) => api.get('/admin/company/reviews', { params }),
  respondToReview: (reviewId, response) => api.put(`/admin/reviews/${reviewId}/respond`, { response }),
};

// Achievements API
export const achievementAPI = {
  getTemplates: () => api.get('/achievements/templates'),
  getCompanyAchievements: (companyId) => api.get(`/companies/${companyId}/achievements`),
  getCompanyProgress: (companyId) => api.get(`/companies/${companyId}/achievements/progress`),
  refreshAchievements: (companyId) => api.post(`/companies/${companyId}/achievements/refresh`),
  seedTemplates: () => api.post('/achievements/templates/seed'),
};

// Service Comments API
// Services are locked after creation - use comments to add additional information
export const serviceCommentsAPI = {
  getComments: (serviceId) => api.get(`/services/${serviceId}/comments`),
  addComment: (serviceId, data) => api.post(`/services/${serviceId}/comments`, data),
  updateComment: (serviceId, commentId, data) => api.put(`/services/${serviceId}/comments/${commentId}`, data),
  deleteComment: (serviceId, commentId) => api.delete(`/services/${serviceId}/comments/${commentId}`),
  getCommentHistory: (serviceId, commentId) => api.get(`/services/${serviceId}/comments/${commentId}/history`),
};

// Support Ticket API
export const ticketAPI = {
  // User endpoints
  getMyTickets: () => api.get('/tickets/'),
  getTicket: (ticketId) => api.get(`/tickets/${ticketId}`),
  createTicket: (data) => api.post('/tickets/', data),
  addResponse: (ticketId, data) => api.post(`/tickets/${ticketId}/respond`, data),
  getTicketTypes: () => api.get('/tickets/types'),

  // Super admin endpoints
  getAllTickets: (params) => api.get('/tickets/admin/all', { params }),
  getTicketAdmin: (ticketId) => api.get(`/tickets/admin/${ticketId}`),
  updateTicket: (ticketId, data) => api.put(`/tickets/admin/${ticketId}`, data),
  assignTicket: (ticketId) => api.post(`/tickets/admin/${ticketId}/assign`),
  resolveTicket: (ticketId, data) => api.post(`/tickets/admin/${ticketId}/resolve`, data),
  closeTicket: (ticketId) => api.post(`/tickets/admin/${ticketId}/close`),
  getTicketStats: () => api.get('/tickets/admin/stats'),
  getSuperAdmins: () => api.get('/tickets/admin/super-admins'),
};

// Strava Integration API
export const stravaAPI = {
  // Connection status
  getConfig: () => api.get('/strava/config'),
  getStatus: () => api.get('/strava/status'),
  getAuthUrl: () => api.get('/strava/auth-url'),
  disconnect: () => api.post('/strava/disconnect'),

  // Activities
  getActivities: (params) => api.get('/strava/activities', { params }),
  getActivityDetail: (activityId) => api.get(`/strava/activities/${activityId}`),

  // Bike-linked activities
  getBikeActivities: (bikeId) => api.get(`/strava/bikes/${bikeId}/activities`),
  addActivityToBike: (bikeId, stravaActivityId) => api.post(`/strava/bikes/${bikeId}/activities`, { strava_activity_id: stravaActivityId }),
  removeActivityFromBike: (bikeId, activityId) => api.delete(`/strava/bikes/${bikeId}/activities/${activityId}`),

  // Strava gear
  getGear: () => api.get('/strava/gear'),
};

// Google Calendar Integration API
export const calendarAPI = {
  // Connection status
  getConfig: () => api.get('/calendar/config'),
  getStatus: () => api.get('/calendar/status'),
  getAuthUrl: () => api.get('/calendar/auth-url'),
  disconnect: () => api.post('/calendar/disconnect'),
  updateSettings: (settings) => api.put('/calendar/settings', settings),

  // Calendars
  getCalendars: () => api.get('/calendar/calendars'),

  // Events
  createEvent: (eventType, referenceId) => api.post('/calendar/events/create', { event_type: eventType, reference_id: referenceId }),
  getEvents: () => api.get('/calendar/events'),
  deleteEvent: (eventId) => api.delete(`/calendar/events/${eventId}`),

  // Generate calendar links (no OAuth required)
  generateLinks: (eventType, referenceId) => api.post('/calendar/generate-link', { event_type: eventType, reference_id: referenceId }),
};

// SuperAdmin Global Settings API
export const settingsAPI = {
  // Get all settings
  getAll: () => api.get('/superadmin/settings'),
  // Get single setting
  get: (key) => api.get(`/superadmin/settings/${key}`),
  // Update settings (pass object of key-value pairs)
  update: (settings) => api.post('/superadmin/settings', settings),
  // Delete a setting
  delete: (key) => api.delete(`/superadmin/settings/${key}`),
  // Test email connection
  testEmail: () => api.post('/superadmin/settings/test-email'),
  // Test OpenAI connection
  testOpenAI: () => api.post('/superadmin/settings/test-openai'),
};

// Email Integration API (Professional tier)
export const emailIntegrationAPI = {
  // Company endpoints
  getStatus: () => api.get('/email-integration/status'),
  getInstructions: () => api.get('/email-integration/instructions'),
  startSetup: (data) => api.post('/email-integration/setup', data),
  acknowledgeInstructions: () => api.post('/email-integration/acknowledge-instructions'),
  uploadSampleReceipt: (formData) => api.post('/email-integration/sample-receipt', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  getSampleReceipts: () => api.get('/email-integration/sample-receipts'),

  // SuperAdmin endpoints
  getAllIntegrations: (status) => api.get('/superadmin/email-integrations', { params: { status } }),
  getIntegrationDetail: (id) => api.get(`/superadmin/email-integrations/${id}`),
  approveIntegration: (id, notes) => api.post(`/superadmin/email-integrations/${id}/approve`, { notes }),
  rejectIntegration: (id, reason, notes) => api.post(`/superadmin/email-integrations/${id}/reject`, { reason, notes }),
  extractSampleReceipt: (sampleId) => api.post(`/superadmin/sample-receipts/${sampleId}/extract`),
  reviewSampleReceipt: (sampleId, approved, notes) => api.post(`/superadmin/sample-receipts/${sampleId}/review`, { approved, notes }),
};

// Square Integration API (Enterprise tier)
export const squareAPI = {
  // OAuth + status
  getStatus: () => api.get('/integrations/square/status'),
  getConnectUrl: () => api.get('/integrations/square/connect'),
  disconnect: () => api.post('/integrations/square/disconnect'),

  // Device mode (terminal / register)
  getDeviceMode: () => api.get('/integrations/square/device-mode'),
  setDeviceMode: (mode) => api.put('/integrations/square/device-mode', { device_mode: mode }),

  // Backward-compat aliases
  connect: () => api.get('/integrations/square/connect'),

  // Catalog & inventory
  getCatalog: (paramsOrRefresh = false) => {
    const params = typeof paramsOrRefresh === 'object'
      ? paramsOrRefresh
      : { refresh: paramsOrRefresh };
    return api.get('/square/catalog', { params });
  },
  getCatalogBundle: (locationId, refresh = false) => api.get('/square/catalog-bundle', {
    params: { location_id: locationId || undefined, refresh }
  }),
  createCatalogItem: (data) => api.post('/square/catalog/items', data),
  updateCatalogItem: (variationId, data) => api.put(`/square/catalog/items/${variationId}`, data),
  deleteCatalogItem: (variationId) => api.delete(`/square/catalog/items/${variationId}`),
  listLocations: () => api.get('/square/locations'),
  getInventory: (locationId, variationIds, refresh = false) => api.get('/square/inventory', {
    params: {
      location_id: locationId,
      variation_ids: Array.isArray(variationIds) && variationIds.length > 0 ? variationIds.join(',') : undefined,
      refresh
    }
  }),

  // Order + payment flow (tickets)
  getSquareTicket: (sessionId) => api.get(`/square/tickets/${sessionId}`),
  createOrder: (data) => api.post('/square/orders', data),
  createCheckoutLinkForService: (serviceId) => api.post(`/square/services/${serviceId}/checkout-link`),
  createTerminalCheckout: (data) => api.post('/square/terminal/checkout', data),
  getTerminalCheckout: (checkoutId) => api.get(`/square/terminal/checkout/${checkoutId}`),
  createCashPayment: (data) => api.post('/square/payments/cash', data),
  payOrder: (data) => api.post('/square/orders/pay', data),
  markOrderPaid: (data) => api.post('/square/orders/pay', data),

  // Incoming orders (from Square POS)
  listIncomingOrders: (showAll = false) => api.get('/square/orders/incoming', { params: { all: showAll ? 'true' : 'false', limit: 100 } }),
  getIncomingOrdersVersion: () => api.get('/square/orders/incoming/version'),
  reconcileIncomingOrders: (data = {}) => api.post('/square/orders/reconcile', data),
  getOrderStats: () => api.get('/square/orders/stats'),
  syncOrders: () => api.post('/square/orders/sync'),
  syncBookings: () => api.post('/integrations/square/sync-bookings'),
  // VIN-based Square invoice sync: attaches Square line items + totals to
  // existing Services by matching the bike's frame number on the Square order.
  syncServiceInvoices: (payload = {}) => api.post('/square/services/sync-invoices', payload),
  linkFrameToOrder: (orderId, data) => api.post(`/square/orders/${orderId}/link-frame`, data),
  dismissOrder: (orderId) => api.post(`/square/orders/${orderId}/dismiss`),
  getOrderDebug: (orderId) => api.get(`/square/orders/${orderId}/debug`),
  cleanupServices: (data = {}) => api.post('/square/services/cleanup', data),
  reconcileDraftServices: (data = {}) => api.post('/square/services/reconcile-drafts', data),
  repairBikes: () => api.post('/square/bikes/repair'),

  // Pending orders
  listPendingOrders: () => api.get('/square/orders/pending'),

  // Terminal devices
  listDevices: (locationId) => api.get('/square/terminal/devices', {
    params: { location_id: locationId || undefined }
  }),
  refreshDevices: (locationId) => api.get('/square/terminal/devices', {
    params: { location_id: locationId || undefined, refresh: 'true' }
  }),
  createPairingCode: (data) => api.post('/square/terminal/pair', data),
  getDeviceStatus: (deviceId) => api.get(`/square/terminal/devices/${deviceId}/status`),
  testPrint: (data) => api.post('/square/terminal/test-print', data),
  /** Send RECEIPT action to a Square Terminal so it prints on its connected printer (e.g. Star TSP100). */
  printReceiptOnTerminal: (data) => api.post('/square/terminal/print-receipt', data),

  // Payment link (card payment without terminal hardware)
  createPaymentLink: (data) => api.post('/square/payment-link', data),
  checkOrderPaymentStatus: (orderId) => api.get(`/square/orders/${orderId}/payment-status`),

  // Backward-compat alias
  createCheckout: (data) => api.post('/square/terminal/checkout', data),

  // Diagnostic endpoints
  diagnose: (runTest = false) => api.get('/square/diagnose', { params: { test: runTest ? 'true' : 'false' } }),
  diagnoseUnlinked: (limit = 5) => api.get('/square/orders/diagnose-unlinked', { params: { limit } }),
};

// Receipt AI Extraction API (Professional+ tier)
export const receiptAPI = {
  // Extract data from receipt image using AI
  extractFromImage: (formData) => api.post('/receipt/extract', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
    timeout: 90000, // 90 seconds - AI extraction can take time
  }),
  // Extract data from multiple receipt images and aggregate
  extractFromBatch: (files) => {
    const formData = new FormData();
    files.forEach(file => {
      formData.append('receipts[]', file);
    });
    return api.post('/receipt/extract-batch', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 120000, // 120 seconds - batch takes longer
    });
  },
};

export default api;
